import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Clock, CheckCircle2, XCircle, Loader2, ArrowRight, Database } from 'lucide-react';
import { mappingApi } from '../../services/api';
import type { MappingRunListItem, MappingRunStatus } from '../../types';

interface Props {
  onNewRun: () => void;
}

const StatusPill: React.FC<{ status: MappingRunStatus }> = ({ status }) => {
  const map = {
    pending: { icon: Clock, text: 'Pending', cls: 'text-cargo-muted bg-cargo-border/30' },
    running: { icon: Loader2, text: 'Running', cls: 'text-cargo-accent bg-cargo-accent/10 animate-pulse' },
    completed: { icon: CheckCircle2, text: 'Completed', cls: 'text-cargo-success bg-cargo-success/10' },
    failed: { icon: XCircle, text: 'Failed', cls: 'text-cargo-error bg-cargo-error/10' },
  } as const;
  const { icon: Icon, text, cls } = map[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      <Icon className={`w-3 h-3 ${status === 'running' ? 'animate-spin' : ''}`} />
      {text}
    </span>
  );
};

const MappingLanding: React.FC<Props> = ({ onNewRun }) => {
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['mapping', 'runs'],
    queryFn: async () => (await mappingApi.listRuns({ limit: 50 })).data,
    refetchInterval: (query) => {
      const runs = query.state.data?.payload?.runs || [];
      const hasActive = runs.some((r: MappingRunListItem) => r.status === 'pending' || r.status === 'running');
      return hasActive ? 3000 : false;
    },
  });

  const runs = data?.payload?.runs ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-cargo-text">Past Mapping Runs</h2>
          <p className="text-cargo-muted text-sm mt-1">
            Source-to-target schema mappings generated by Cargo engine
          </p>
        </div>
        <button
          onClick={onNewRun}
          className="inline-flex items-center gap-2 px-4 py-2 bg-cargo-accent text-white rounded-lg hover:bg-cargo-accent/90 transition-colors font-medium text-sm"
        >
          <Plus className="w-4 h-4" />
          New Mapping Run
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3">
        <SummaryCard label="Total Runs" value={data?.payload?.total ?? 0} />
        <SummaryCard label="Completed" value={runs.filter((r) => r.status === 'completed').length} />
        <SummaryCard label="Running" value={runs.filter((r) => r.status === 'running' || r.status === 'pending').length} />
        <SummaryCard label="Approved" value={runs.filter((r) => r.approval_status === 'approved').length} />
      </div>

      {/* Runs table */}
      <div className="bg-cargo-panel border border-cargo-border rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-cargo-accent animate-spin" />
          </div>
        ) : error ? (
          <div className="p-6 text-cargo-error text-sm">Failed to load runs.</div>
        ) : runs.length === 0 ? (
          <div className="p-12 text-center">
            <Database className="w-12 h-12 text-cargo-muted mx-auto mb-3" />
            <p className="text-cargo-text font-medium">No mapping runs yet</p>
            <p className="text-cargo-muted text-sm mt-1">
              Create your first run to generate source-to-target mappings with AI
            </p>
          </div>
        ) : (
          <table className="w-full text-sm table-fixed">
            <thead className="bg-cargo-surface border-b border-cargo-border text-left text-cargo-muted text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 w-[18%]">Run</th>
                <th className="px-4 py-3 w-[10%]">Status</th>
                <th className="px-4 py-3 w-[18%]">Source</th>
                <th className="px-4 py-3 w-[18%]">Target</th>
                <th className="px-4 py-3 w-[9%]">Coverage</th>
                <th className="px-4 py-3 w-[10%]">Confidence</th>
                <th className="px-4 py-3 w-[13%]">Created</th>
                <th className="px-4 py-3 w-[4%]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cargo-border">
              {runs.map((r) => (
                <tr key={r.run_id} className="hover:bg-cargo-hover/20 cursor-pointer" onClick={() => navigate(`/mapping/runs/${r.run_id}`)}>
                  <td className="px-4 py-3">
                    <div className="font-mono text-xs text-cargo-text truncate" title={r.run_id}>{r.run_id}</div>
                    {r.description && <div className="text-cargo-muted text-xs mt-0.5 truncate" title={r.description}>{r.description}</div>}
                  </td>
                  <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                  <td
                    className="px-4 py-3 font-mono text-xs text-cargo-muted truncate"
                    title={r.source?.db_uri_sanitized ?? ''}
                  >
                    {r.source?.db_uri_sanitized ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <span className="text-cargo-muted">{r.target?.mode}</span>
                    {r.target?.db_uri_sanitized && (
                      <div
                        className="font-mono text-xs text-cargo-muted mt-0.5 truncate"
                        title={r.target.db_uri_sanitized}
                      >
                        {r.target.db_uri_sanitized}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {r.coverage_percentage != null ? `${r.coverage_percentage}%` : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {r.average_confidence != null ? `${(r.average_confidence * 100).toFixed(0)}%` : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-cargo-muted">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <ArrowRight className="w-4 h-4 text-cargo-muted" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const SummaryCard: React.FC<{ label: string; value: number | string }> = ({ label, value }) => (
  <div className="bg-cargo-panel border border-cargo-border rounded-lg p-4">
    <div className="text-cargo-muted text-xs font-medium uppercase tracking-wider">{label}</div>
    <div className="text-2xl font-semibold text-cargo-text mt-1">{value}</div>
  </div>
);

export default MappingLanding;
